/**
 * Visa Constants Package
 *
 * Single source of truth for visa criteria definitions and document types.
 * This package prevents mismatches between API and workers by providing
 * centralized, typed constants.
 *
 * @packageDocumentation
 */

// Export types
export * from './types';

// Export document types
export * from './documentTypes';

// Export criteria eligibility matrix
export * from './criteriaEligibility';

// Export O-1A criteria
export * from './o1a';

// Export O-1B criteria
export * from './o1b';

// Export EB-1A criteria
export * from './eb1a';

// Export EB-1B criteria
export * from './eb1b';

// Export EB-1C criteria
export * from './eb1c';

// Export EB-2 criteria
export * from './eb2';

// Export EB-2 NIW criteria
export * from './eb2niw';

import { O1A_CONFIG } from './o1a';
import { O1B_CONFIG } from './o1b';
import { EB1A_CONFIG } from './eb1a';
import { EB1B_CONFIG } from './eb1b';
import { EB1C_CONFIG } from './eb1c';
import { EB2_CONFIG } from './eb2';
import { EB2NIW_CONFIG } from './eb2niw';
import { VisaCriteriaMap, VisaCriterion } from './types';

/**
 * Map of all visa types to their criteria
 */
export const VISA_CRITERIA_MAP: VisaCriteriaMap = {
  'O-1A': O1A_CONFIG.criteria,
  'O-1B': O1B_CONFIG.criteria,
  'EB-1A': EB1A_CONFIG.criteria,
  'EB-1B': EB1B_CONFIG.criteria,
  'EB-1C': EB1C_CONFIG.criteria,
  'EB-2': EB2_CONFIG.criteria,
  'EB-2 NIW': EB2NIW_CONFIG.criteria,
};

/**
 * Get criteria for a specific visa type
 *
 * @param visaType - The visa type (e.g., 'O-1A', 'EB-1A')
 * @returns Array of criteria for the visa type, or empty array if not found
 *
 * @example
 * const criteria = getCriteriaForVisa('O-1A');
 * console.log(criteria.length); // 8
 */
export function getCriteriaForVisa(visaType: string): VisaCriterion[] {
  return VISA_CRITERIA_MAP[visaType] || [];
}

/**
 * Get all supported visa types
 *
 * @returns Array of supported visa type codes
 *
 * @example
 * const types = getSupportedVisaTypes();
 * console.log(types); // ['O-1A', 'EB-1A', 'EB-2 NIW']
 */
export function getSupportedVisaTypes(): string[] {
  return Object.keys(VISA_CRITERIA_MAP);
}

/**
 * Check if a visa type is supported
 *
 * @param visaType - The visa type to check
 * @returns True if the visa type has criteria defined
 *
 * @example
 * isVisaTypeSupported('O-1A'); // true
 * isVisaTypeSupported('H-1B'); // false
 */
export function isVisaTypeSupported(visaType: string): boolean {
  return visaType in VISA_CRITERIA_MAP;
}

/**
 * Find a specific criterion by ID across all visa types
 *
 * @param criterionId - The criterion ID to find
 * @returns The criterion and its visa type, or null if not found
 *
 * @example
 * const result = findCriterionById('o1a_awards');
 * console.log(result?.visaType); // 'O-1A'
 * console.log(result?.criterion.name); // 'Awards and Recognition'
 */
export function findCriterionById(
  criterionId: string
): { visaType: string; criterion: VisaCriterion } | null {
  for (const [visaType, criteria] of Object.entries(VISA_CRITERIA_MAP)) {
    const criterion = criteria.find((c) => c.id === criterionId);
    if (criterion) {
      return { visaType, criterion };
    }
  }
  return null;
}
